import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

const MAX_TRIES = 15;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refetch } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | done | timeout | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0, active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (!active) return;
        if (data.payment_status === "paid") {
          setState("done");
          await refetch();
          setTimeout(() => nav("/app", { replace: true }), 1200);
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (!active) return;
      if (++tries >= MAX_TRIES) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-navy grain-navy flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 text-center" data-testid="billing-result">
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-blue-600" size={40} />
            <h1 className="mt-4 text-xl font-bold text-navy">Confirming your billing…</h1>
            <p className="mt-2 text-sm text-slate-500">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "done" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-500" size={48} data-testid="billing-success-icon" />
            <h1 className="mt-4 text-2xl font-bold text-navy">You're all set!</h1>
            <p className="mt-2 text-sm text-slate-600">Your workspace is ready. Taking you in…</p>
          </>
        )}
        {state === "timeout" && (
          <>
            <Loader2 className="mx-auto text-amber-500" size={40} />
            <h1 className="mt-4 text-xl font-bold text-navy">Still processing</h1>
            <p className="mt-2 text-sm text-slate-600">Your payment is confirming. Refresh in a moment — your workspace will unlock automatically.</p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertTriangle className="mx-auto text-red-500" size={40} />
            <h1 className="mt-4 text-xl font-bold text-navy">We couldn't confirm billing</h1>
            <p className="mt-2 text-sm text-slate-600">Please try again from the billing page.</p>
            <button onClick={() => nav("/billing", { replace: true })} data-testid="billing-retry" className="mt-5 px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">Back to billing</button>
          </>
        )}
      </div>
    </div>
  );
}
